import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Paper, Typography, Grid, Stack, Chip, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TextField, InputAdornment, IconButton,
  LinearProgress, Collapse, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Alert,
} from '@mui/material';
import {
  Search, Refresh, ExpandMore, ExpandLess,
  WarningAmber, ErrorOutline, CheckCircle, InfoOutlined,
  Inventory2, TrendingDown, CalendarMonth, Storefront,
} from '@mui/icons-material';
import { consumableStockAnalysisUrl, consumableBatchesByIdUrl } from '../BaseURL';

const PRIMARY = '#41a386';
const BORDER  = '#e5e9ef';
const MUTED   = '#64748b';
const STRONG  = '#0f172a';
const LABEL_SX = {
  fontSize: '0.66rem', fontWeight: 700, color: '#94a3b8',
  textTransform: 'uppercase', letterSpacing: '0.6px',
};

const LEVEL = {
  critical: { color: '#dc2626', tint: 'rgba(220,38,38,0.10)',  strong: '#991B1B', label: 'Critical', Icon: ErrorOutline },
  warning:  { color: '#d97706', tint: 'rgba(217,119,6,0.10)',  strong: '#92400E', label: 'Warning',  Icon: WarningAmber },
  notice:   { color: '#ca8a04', tint: 'rgba(202,138,4,0.10)',  strong: '#854D0E', label: 'Notice',   Icon: InfoOutlined },
  ok:       { color: '#10b981', tint: 'rgba(16,185,129,0.10)', strong: '#15803D', label: 'OK',       Icon: CheckCircle },
};

const formatGHS = (n) =>
  new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(Number(n) || 0);

const StatusPill = ({ level }) => {
  const lv = LEVEL[level] || LEVEL.ok;
  const I = lv.Icon;
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5,
      px: 1, py: 0.25, borderRadius: 1,
      bgcolor: lv.tint, color: lv.strong,
      fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
    }}>
      <I sx={{ fontSize: 12 }} />
      {lv.label}
    </Box>
  );
};

const ExpiryBadge = ({ date }) => {
  if (!date) {
    return (
      <Box sx={{ display: 'inline-flex', px: 1, py: 0.25, borderRadius: 1, bgcolor: '#f1f5f9', color: MUTED, fontSize: '0.7rem', fontWeight: 700 }}>
        No expiry
      </Box>
    );
  }
  const days = Math.round((new Date(date) - new Date()) / 86400000);
  let bg = LEVEL.ok.tint, color = LEVEL.ok.strong, text = new Date(date).toLocaleDateString();
  if (days < 0) {
    bg = LEVEL.critical.tint; color = LEVEL.critical.strong;
    text = `Expired ${new Date(date).toLocaleDateString()}`;
  } else if (days <= 90) {
    bg = LEVEL.warning.tint; color = LEVEL.warning.strong;
    text = `Exp in ${days}d`;
  }
  return (
    <Box sx={{ display: 'inline-flex', px: 1, py: 0.25, borderRadius: 1, bgcolor: bg, color, fontSize: '0.7rem', fontWeight: 700 }}>
      {text}
    </Box>
  );
};

const ConsumptionBar = ({ pct, alertLevel }) => {
  const color = LEVEL[alertLevel]?.color || LEVEL.ok.color;
  return (
    <Box sx={{ width: '100%', minWidth: 130 }}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
        <Typography sx={{ fontSize: '0.66rem', color: MUTED, fontWeight: 600 }}>Consumed</Typography>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color }}>{pct}%</Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={Math.min(pct, 100)}
        sx={{
          height: 6, borderRadius: 3,
          bgcolor: '#f1f5f9',
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 }
        }}
      />
    </Box>
  );
};

const StatTile = ({ label, value, icon, tint, color, active, onClick }) => (
  <Paper
    onClick={onClick}
    elevation={0}
    sx={{
      p: 1.5, borderRadius: 2, cursor: onClick ? 'pointer' : 'default',
      border: `1px solid ${active ? color : BORDER}`,
      outline: active ? `2px solid ${color}` : 'none',
      outlineOffset: -1,
      bgcolor: '#fff',
      display: 'flex', alignItems: 'center', gap: 1.5,
      transition: 'transform .15s ease',
      '&:hover': onClick ? { transform: 'translateY(-1px)' } : {},
    }}>
    <Box sx={{
      width: 36, height: 36, borderRadius: 1.5,
      bgcolor: tint, color,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {icon}
    </Box>
    <Box>
      <Typography sx={{ ...LABEL_SX, mb: 0.25 }}>{label}</Typography>
      <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: STRONG, lineHeight: 1 }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

const ConsumableStockAnalysis = () => {
  const [rows, setRows]           = useState([]);
  const [summary, setSummary]     = useState({ tracked: 0, critical: 0, warning: 0, notice: 0, ok: 0 });
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);

  const [search, setSearch]               = useState('');
  const [searchInput, setSearchInput]     = useState('');
  const [levelFilter, setLevelFilter]     = useState('all');

  const [page, setPage]                   = useState(0);
  const [rowsPerPage, setRowsPerPage]     = useState(15);

  const [expandedId, setExpandedId]       = useState(null);
  const [batchesById, setBatchesById]     = useState({});  // id -> array
  const [batchesLoadingFor, setBatchesLoadingFor] = useState(null);

  // Debounce
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(consumableStockAnalysisUrl);
      if (data?.success) {
        setRows(data.results || []);
        setSummary(data.summary || {});
      } else {
        setError(data?.message || 'Failed to load analysis');
        setRows([]);
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleExpand = async (row) => {
    if (expandedId === row.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(row.id);
    if (!batchesById[row.id]) {
      setBatchesLoadingFor(row.id);
      try {
        const { data } = await axios.get(consumableBatchesByIdUrl, { params: { id: row.id } });
        if (data?.success) {
          setBatchesById(prev => ({ ...prev, [row.id]: data.results || [] }));
        }
      } catch (err) {
        console.error('[consumable analysis] batches load failed:', err.message);
      } finally {
        setBatchesLoadingFor(null);
      }
    }
  };

  // Apply filters client-side (data set is bounded by # of consumables).
  const filtered = useMemo(() => {
    return rows.filter(r => {
      if (levelFilter !== 'all' && r.alert_level !== levelFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        const blob = `${r.name} ${r.category || ''}`.toLowerCase();
        if (!blob.includes(s)) return false;
      }
      return true;
    });
  }, [rows, search, levelFilter]);

  const pagedRows = filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const totalValueOnHand = filtered.reduce(
    (s, r) => s + (Number(r.stock_qty) || 0) * (Number(r.cost_per_unit) || 0),
    0
  );

  return (
    <Box sx={{ p: 2, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
            <Inventory2 sx={{ color: PRIMARY, fontSize: 18 }} />
            <Typography sx={{ ...LABEL_SX, color: PRIMARY }}>
              Procurement · Consumables
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: STRONG, lineHeight: 1.15 }}>
            Consumable Stock Analysis
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: MUTED, mt: 0.5 }}>
            Live consumption, reorder + expiry signals per consumable. Click any row to drill into its purchase batches.
          </Typography>
        </Box>
        <Tooltip title="Reload">
          <span>
            <IconButton size="small" onClick={fetchData} disabled={loading}
              sx={{ border: `1px solid ${BORDER}`, bgcolor: '#fff', color: MUTED }}>
              {loading ? <CircularProgress size={14} sx={{ color: PRIMARY }} /> : <Refresh fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Stat tiles (clickable filters) */}
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatTile label="Tracked" value={summary.tracked || 0}
            icon={<Storefront sx={{ fontSize: 20 }} />} tint="rgba(65,163,134,0.10)" color={PRIMARY}
            active={levelFilter === 'all'} onClick={() => setLevelFilter('all')} />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatTile label="Critical" value={summary.critical || 0}
            icon={<ErrorOutline sx={{ fontSize: 20 }} />} tint={LEVEL.critical.tint} color={LEVEL.critical.strong}
            active={levelFilter === 'critical'} onClick={() => setLevelFilter('critical')} />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatTile label="Warning" value={summary.warning || 0}
            icon={<WarningAmber sx={{ fontSize: 20 }} />} tint={LEVEL.warning.tint} color={LEVEL.warning.strong}
            active={levelFilter === 'warning'} onClick={() => setLevelFilter('warning')} />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatTile label="Notice" value={summary.notice || 0}
            icon={<InfoOutlined sx={{ fontSize: 20 }} />} tint={LEVEL.notice.tint} color={LEVEL.notice.strong}
            active={levelFilter === 'notice'} onClick={() => setLevelFilter('notice')} />
        </Grid>
        <Grid item xs={12} sm={8} md={2.4}>
          <StatTile label="OK" value={summary.ok || 0}
            icon={<CheckCircle sx={{ fontSize: 20 }} />} tint={LEVEL.ok.tint} color={LEVEL.ok.strong}
            active={levelFilter === 'ok'} onClick={() => setLevelFilter('ok')} />
        </Grid>
      </Grid>

      {/* Filter strip */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 1.5, bgcolor: '#fff', border: `1px solid ${BORDER}`, borderRadius: 2 }}>
        <Stack direction="row" spacing={1.25} flexWrap="wrap" alignItems="center">
          <TextField
            size="small" placeholder="Search by name or category…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: 300, flex: 1 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: MUTED }} /></InputAdornment>,
            }}
          />
          <Typography sx={{ fontSize: '0.78rem', color: MUTED, ml: 'auto' }}>
            On-hand value: <strong style={{ color: STRONG }}>{formatGHS(totalValueOnHand)}</strong>
          </Typography>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Main table */}
      <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden', bgcolor: '#fff' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1.25 }}></TableCell>
                <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1.25 }}>Item</TableCell>
                <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1.25 }}>Batches</TableCell>
                <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1.25 }}>On hand</TableCell>
                <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1.25 }}>Received (lifetime)</TableCell>
                <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1.25, minWidth: 160 }}>Consumption</TableCell>
                <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1.25 }} align="center">Earliest expiry</TableCell>
                <TableCell sx={{ ...LABEL_SX, bgcolor: '#f8fafc', py: 1.25 }} align="center">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ py: 6, textAlign: 'center', color: MUTED }}>
                    <Inventory2 sx={{ fontSize: 36, color: '#cbd5e1', mb: 1 }} />
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: STRONG }}>
                      No consumables to analyse
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem', mt: 0.5 }}>
                      {search || levelFilter !== 'all' ? 'Try clearing the filters.' : 'Register consumables and receive purchases to start tracking.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : pagedRows.map((r) => {
                const isOpen = expandedId === r.id;
                return (
                  <React.Fragment key={r.id}>
                    <TableRow hover sx={{ '& > *': { borderBottom: isOpen ? 'unset' : undefined } }}>
                      <TableCell sx={{ py: 1.2, width: 36 }}>
                        <IconButton size="small" onClick={() => toggleExpand(r)} sx={{ color: MUTED }}>
                          {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ py: 1.2 }}>
                        <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: STRONG }}>
                          {r.name}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 0.25, flexWrap: 'wrap' }}>
                          {r.category && (
                            <Chip size="small" label={r.category}
                              sx={{ height: 18, fontSize: '0.66rem', bgcolor: '#f1f5f9', color: STRONG }} />
                          )}
                          <Typography sx={{ fontSize: '0.7rem', color: MUTED }}>
                            {r.pack_size > 1
                              ? `${r.pack_size} ${r.base_unit}s per ${(r.pack_label || 'pack').toLowerCase()}`
                              : `Single ${r.base_unit}`}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ py: 1.2, fontVariantNumeric: 'tabular-nums' }}>
                        <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, color: STRONG }}>
                          {r.batch_count}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.2, fontVariantNumeric: 'tabular-nums' }}>
                        <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, color: STRONG }}>
                          {r.stock_qty} {r.base_unit}{r.stock_qty === 1 ? '' : 's'}
                        </Typography>
                        {r.reorder_level > 0 && (
                          <Typography sx={{ fontSize: '0.68rem', color: MUTED }}>
                            reorder ≤ {r.reorder_level}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1.2, fontVariantNumeric: 'tabular-nums' }}>
                        <Typography sx={{ fontSize: '0.82rem', color: STRONG }}>
                          {r.total_received}
                        </Typography>
                        {r.total_invested > 0 && (
                          <Typography sx={{ fontSize: '0.68rem', color: MUTED }}>
                            {formatGHS(r.total_invested)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1.2, minWidth: 160 }}>
                        <ConsumptionBar pct={r.consumed_pct} alertLevel={r.alert_level} />
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1.2 }}>
                        <ExpiryBadge date={r.earliest_expiry} />
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1.2 }}>
                        <StatusPill level={r.alert_level} />
                      </TableCell>
                    </TableRow>

                    {/* Expanded — per-batch detail */}
                    <TableRow>
                      <TableCell colSpan={8} sx={{ p: 0, borderBottom: isOpen ? `1px solid ${BORDER}` : undefined }}>
                        <Collapse in={isOpen} unmountOnExit>
                          <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#f8fafc' }}>
                            <Typography sx={{ ...LABEL_SX, color: PRIMARY, mb: 1 }}>
                              Active batches · {(batchesById[r.id] || []).length}
                            </Typography>
                            {batchesLoadingFor === r.id ? (
                              <Box sx={{ p: 2, textAlign: 'center' }}>
                                <CircularProgress size={18} sx={{ color: PRIMARY }} />
                              </Box>
                            ) : (batchesById[r.id] || []).length === 0 ? (
                              <Typography sx={{ fontSize: '0.78rem', color: MUTED }}>
                                No batches recorded yet — receive a purchase from Consumable Purchases.
                              </Typography>
                            ) : (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ ...LABEL_SX }}>Purchase date</TableCell>
                                    <TableCell sx={{ ...LABEL_SX }}>Supplier / invoice</TableCell>
                                    <TableCell sx={{ ...LABEL_SX }}>Qty</TableCell>
                                    <TableCell sx={{ ...LABEL_SX }}>Unit cost</TableCell>
                                    <TableCell sx={{ ...LABEL_SX }}>Total</TableCell>
                                    <TableCell sx={{ ...LABEL_SX }} align="center">Expiry</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {batchesById[r.id].map(b => (
                                    <TableRow key={b.trans_code} hover>
                                      <TableCell sx={{ fontSize: '0.78rem', color: STRONG, fontVariantNumeric: 'tabular-nums' }}>
                                        {b.purchase_date}
                                      </TableCell>
                                      <TableCell sx={{ fontSize: '0.78rem', color: STRONG }}>
                                        {b.supplier || <span style={{ color: '#cbd5e1' }}>—</span>}
                                        {b.invoice_no && (
                                          <Typography sx={{ fontSize: '0.68rem', color: MUTED }}># {b.invoice_no}</Typography>
                                        )}
                                      </TableCell>
                                      <TableCell sx={{ fontSize: '0.78rem', color: STRONG, fontVariantNumeric: 'tabular-nums' }}>
                                        {b.batch_qty} {r.base_unit}{b.batch_qty === 1 ? '' : 's'}
                                        {(b.pack_qty > 0 || b.loose_qty > 0) && (
                                          <Typography sx={{ fontSize: '0.66rem', color: MUTED }}>
                                            {b.pack_qty > 0 ? `${b.pack_qty}×${r.pack_label || 'pack'}` : ''}
                                            {b.pack_qty > 0 && b.loose_qty > 0 ? ' + ' : ''}
                                            {b.loose_qty > 0 ? `${b.loose_qty} loose` : ''}
                                          </Typography>
                                        )}
                                      </TableCell>
                                      <TableCell sx={{ fontSize: '0.78rem', color: STRONG, fontVariantNumeric: 'tabular-nums' }}>
                                        {formatGHS(b.unit_cost)}
                                      </TableCell>
                                      <TableCell sx={{ fontSize: '0.82rem', fontWeight: 700, color: STRONG, fontVariantNumeric: 'tabular-nums' }}>
                                        {formatGHS(b.total_cost)}
                                      </TableCell>
                                      <TableCell align="center">
                                        <ExpiryBadge date={b.expiry_date} />
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 15, 25, 50]}
        />
      </Paper>
    </Box>
  );
};

export default ConsumableStockAnalysis;
